import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { F0AvatarList } from '@factorialco/f0-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { amountToNumber, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useBulkUpsertSpendingLimits } from 'modules/benefits/lib/useBulkUpsertSpendingLimits'

import { SpendingLimitOverride } from '../../types'

interface BulkEditSpendingLimitModalProps {
  isOpen: boolean
  selectedItems: SpendingLimitOverride[]
  onClose: () => void
  onSave: (newLimit: number) => void
  defaultLimits: {
    meals: number
    transportation: number
  }
}

export const BulkEditSpendingLimitModal = ({
  isOpen,
  selectedItems,
  onClose,
  onSave,
  defaultLimits,
}: BulkEditSpendingLimitModalProps) => {
  const [newLimit, setNewLimit] = React.useState<string>('')
  const { upsertLimits } = useBulkUpsertSpendingLimits()

  const handleSave = async () => {
    const newLimitAmount = amountToNumber(newLimit)
    if (isNaN(newLimitAmount) || newLimitAmount < 0) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.invalid_amount'
        ),
      })
      return
    }

    const adjustedLimitAmount = toCents(newLimitAmount)

    const exceedsDefault = selectedItems.some((item) => {
      const defaultLimit =
        item.category === 'meals'
          ? defaultLimits.meals
          : item.category === 'transportation'
          ? defaultLimits.transportation
          : 0
      return adjustedLimitAmount > defaultLimit
    })

    if (exceedsDefault) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.surpassed_default_limit'
        ),
      })
      return
    }

    const overrides = selectedItems.map((item) => ({
      employeeId: item.employeeId,
      category: item.category,
      amountCents: adjustedLimitAmount,
    }))
    await upsertLimits(overrides, () => {
      onSave(adjustedLimitAmount)
      setNewLimit('')
      onClose()
    })
  }

  const handleClose = () => {
    setNewLimit('')
    onClose()
  }

  if (!isOpen) return null

  const safeSelectedItems = selectedItems || []

  return (
    <Modal
      title={i18n.t(
        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_action_title'
      )}
      description={i18n.t(
        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_action_description'
      )}
      onClose={handleClose}
      onSubmit={handleSave}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" submit>
            {i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_modal_button'
            )}
          </Button>
        </>
      }
    >
      {safeSelectedItems.length > 0 && (
        <F0AvatarList
          avatars={safeSelectedItems.map((item) => ({
            firstName: item.employeeName.split(' ')[0] || '',
            lastName: item.employeeName.split(' ').slice(1).join(' ') || '',
            src: item.employeeAvatar,
            type: 'person',
          }))}
          size="md"
          type="person"
        />
      )}

      <TextField
        label={i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_modal_label'
        )}
        placeholder={i18n.t(
          'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_modal_placeholder'
        )}
        value={newLimit}
        onChange={setNewLimit}
        type="number"
      />
    </Modal>
  )
}
